import { useEffect, useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import toast from 'react-hot-toast'
import { Mail, ArrowLeft, Shield } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useAuth } from '@/providers/auth-provider'
import { useLogin, useMagicLinkRequest, useResendVerification } from '@/hooks/use-auth-api'
import { LoginForm as LoginFormType, MagicLinkRequestForm } from '@/types'
import { OAuthButtons } from '@/components/auth/oauth-buttons'
import { useRecaptcha, extractRateLimitError, isRecaptchaError } from '@/hooks/use-recaptcha'

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
})

const magicLinkSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
})

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated } = useAuth()
  const loginMutation = useLogin()
  const magicLinkMutation = useMagicLinkRequest()
  const { executeRecaptcha, isEnabled: recaptchaEnabled } = useRecaptcha({ action: 'login' })

  const resendVerificationMutation = useResendVerification()
  const [showMagicLink, setShowMagicLink] = useState(false)
  const [magicLinkSent, setMagicLinkSent] = useState(false)
  const [sentEmail, setSentEmail] = useState('')
  const [emailNotVerified, setEmailNotVerified] = useState(false)
  const [unverifiedEmail, setUnverifiedEmail] = useState('')

  const from = location.state?.from?.pathname || '/dashboard'

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true })
    }
  }, [isAuthenticated, navigate, from])

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormType>({
    resolver: zodResolver(loginSchema),
  })

  const {
    register: registerMagicLink,
    handleSubmit: handleMagicLinkSubmit,
    formState: { errors: magicLinkErrors },
  } = useForm<MagicLinkRequestForm>({
    resolver: zodResolver(magicLinkSchema),
  })

  const onSubmit = async (data: LoginFormType) => {
    try {
      // Generate reCAPTCHA token if enabled
      let recaptcha_token: string | null = null
      if (recaptchaEnabled) {
        recaptcha_token = await executeRecaptcha()
      }

      const result = await loginMutation.mutateAsync({
        ...data,
        recaptcha_token,
      } as LoginFormType & { recaptcha_token: string | null })

      // Check if MFA is required
      if (result.mfa_required) {
        toast.success('MFA verification required', {
          icon: <Shield className="w-5 h-5" />,
          duration: 3000,
        })
        navigate('/auth/mfa-challenge', {
          state: { email: data.email, mfa_challenge_token: result.mfa_challenge_token },
          replace: true,
        })
        return
      }

      // Normal login flow (no MFA)
      toast.success('Welcome back!')
      navigate(from, { replace: true })
    } catch (error: any) {
      // Handle rate limit errors (including account lockout)
      const rateLimitError = extractRateLimitError(error)
      if (rateLimitError.isRateLimited) {
        if (rateLimitError.isAccountLocked) {
          toast.error(rateLimitError.message, { duration: 8000 })
        } else {
          toast.error(rateLimitError.message, { duration: 6000 })
        }
        return
      }

      // Handle reCAPTCHA verification errors
      if (isRecaptchaError(error)) {
        toast.error('Security check failed. Please try again.', { duration: 5000 })
        return
      }

      // Handle email not verified error
      if (error.name === 'EMAIL_NOT_VERIFIED' ||
          error.message?.includes('verify your email')) {
        setEmailNotVerified(true)
        setUnverifiedEmail(data.email)
        return
      }

      toast.error(error.message || 'Login failed')
    }
  }

  const onMagicLinkSubmit = async (data: MagicLinkRequestForm) => {
    try {
      await magicLinkMutation.mutateAsync(data)
      setMagicLinkSent(true)
      setSentEmail(data.email)
      toast.success('Check your email for a login link!')
    } catch (error: any) {
      // Handle rate limit errors
      const rateLimitError = extractRateLimitError(error)
      if (rateLimitError.isRateLimited) {
        toast.error(rateLimitError.message, { duration: 6000 })
        return
      }
      toast.error(error.message || 'Failed to send magic link')
    }
  }

  const onResendVerification = async () => {
    try {
      await resendVerificationMutation.mutateAsync(unverifiedEmail)
      toast.success('Verification email sent. Please check your inbox.')
    } catch (error: any) {
      toast.error(error.message || 'Failed to resend verification email')
    }
  }

  // Email not verified view
  if (emailNotVerified) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center">
                <Mail className="w-8 h-8 text-yellow-600" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-center">
              Email verification required
            </CardTitle>
            <CardDescription className="text-center">
              Please check your email for a verification link before signing in.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground text-center">
              We sent a verification email to <span className="font-medium text-foreground">{unverifiedEmail}</span>.
              Click the link in the email to verify your account.
            </p>
            <div className="space-y-2">
              <Button
                className="w-full"
                onClick={onResendVerification}
                loading={resendVerificationMutation.isPending}
              >
                <Mail className="mr-2 h-4 w-4" />
                Resend verification email
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setEmailNotVerified(false)
                  setUnverifiedEmail('')
                }}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Magic link sent confirmation view
  if (magicLinkSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <Mail className="w-8 h-8 text-primary" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-center">
              Check your email
            </CardTitle>
            <CardDescription className="text-center">
              We sent a login link to <span className="font-medium text-foreground">{sentEmail}</span>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground text-center">
              Click the link in your email to sign in. The link will expire in 15 minutes.
            </p>
            <div className="space-y-2">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setMagicLinkSent(false)
                  setSentEmail('')
                }}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to login
              </Button>
              <Button
                variant="ghost"
                className="w-full"
                onClick={() => onMagicLinkSubmit({ email: sentEmail })}
                disabled={magicLinkMutation.isPending}
              >
                Resend email
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Magic link form view
  if (showMagicLink) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center">
              Sign in with email
            </CardTitle>
            <CardDescription className="text-center">
              We'll send you a magic link to sign in without a password
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleMagicLinkSubmit(onMagicLinkSubmit)} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="magic-email" className="text-sm font-medium">
                  Email
                </label>
                <Input
                  id="magic-email"
                  type="email"
                  placeholder="you@example.com"
                  autoComplete="email"
                  error={magicLinkErrors.email?.message}
                  {...registerMagicLink('email')}
                />
              </div>

              <Button
                type="submit"
                className="w-full"
                loading={magicLinkMutation.isPending}
              >
                <Mail className="mr-2 h-4 w-4" />
                Send magic link
              </Button>
            </form>

            <div className="mt-6">
              <Button
                variant="ghost"
                className="w-full"
                onClick={() => setShowMagicLink(false)}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to password login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">
            Welcome back
          </CardTitle>
          <CardDescription className="text-center">
            Sign in to your account to continue
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium">
                Email
              </label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                autoComplete="email"
                error={errors.email?.message}
                {...register('email')}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="text-sm font-medium">
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-primary hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                autoComplete="current-password"
                error={errors.password?.message}
                {...register('password')}
              />
            </div>

            <Button
              type="submit"
              className="w-full"
              loading={loginMutation.isPending}
            >
              Sign In
            </Button>
          </form>

          {/* OAuth login buttons */}
          <div className="mt-6">
            <OAuthButtons mode="login" />
          </div>

          {/* Magic link login option */}
          <div className="mt-4">
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => setShowMagicLink(true)}
            >
              <Mail className="mr-2 h-4 w-4" />
              Sign in with email link
            </Button>
          </div>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">
                  Don't have an account?
                </span>
              </div>
            </div>
            <div className="mt-4 text-center">
              <Link
                to="/register"
                className="text-sm text-primary hover:underline"
              >
                Create an account
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
